import { type ReactNode } from 'react';
import { cn } from '../lib/utils.js';

interface ErrorStateProps {
  /** Short, sentence-case title. Defaults to a generic failure line. */
  title?: string;
  /** One-sentence detail — usually the extracted server/network message. */
  description?: ReactNode;
  /** Recovery CTA — typically a `<Button>` that re-runs the query. */
  action?: ReactNode;
  /** Optional icon override; a danger alert-triangle is the default. */
  icon?: ReactNode;
  className?: string;
}

/**
 * Error-state primitive — the counterpart to `EmptyState`. Use it whenever a
 * query can fail, so a failed fetch reads as a *problem with a way out*, never
 * as "no data." Before this existed, several ops-critical surfaces (the live
 * board, the report shell, the site/camp board) rendered a failed request
 * identically to an empty one — "server down" and "nobody's here" looked the
 * same. A danger-tinted mark + the extracted message + a Try-again action fix
 * that. Drop it into a Card the same way you drop in `EmptyState`.
 */
export function ErrorState({
  title = 'Something went wrong',
  description,
  action,
  icon,
  className,
}: ErrorStateProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-6 py-12 text-center',
        className
      )}
      role="alert"
    >
      {/* `text-danger-on-surface`, not `text-danger`: this is ink on the pale
          danger tint, which is exactly the `-on-surface` role. `text-danger`
          (the solid fill colour) measured ~4.4:1 here in light — under the bar
          for a 24px glyph carrying the whole meaning of the state. */}
      <div className="mb-component flex h-12 w-12 items-center justify-center rounded-full bg-danger-surface text-danger-on-surface">
        {icon ?? (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </svg>
        )}
      </div>
      <p className="text-h3 text-foreground">{title}</p>
      {description && (
        <p className="mt-element max-w-md text-copy text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-component">{action}</div>}
    </div>
  );
}
